<template>
  <Teleport to="body">
    <Transition name="action-confirm-dialog">
      <div v-if="visible" class="action-confirm-dialog">
        <div class="action-confirm-dialog__mask" @click="handleClose" />
        <div
          class="action-confirm-dialog__panel"
          role="dialog"
          aria-modal="true"
          :aria-label="title"
          @click.stop
        >
          <div class="action-confirm-dialog__header">
            <div class="action-confirm-dialog__title">{{ title }}</div>
            <div v-if="content" class="action-confirm-dialog__content">{{ content }}</div>
          </div>
          <div v-if="showCancel || showOk" class="action-confirm-dialog__actions">
            <button v-if="showCancel" type="button" @click="handleCancel">{{ cancelText }}</button>
            <button v-if="showOk" type="button" @click="handleConfirm">{{ okText }}</button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    required: true,
  },
  content: {
    type: String,
    default: '',
  },
  showCancel: {
    type: Boolean,
    default: true,
  },
  showOk: {
    type: Boolean,
    default: true,
  },
  cancelText: {
    type: String,
    default: '取消',
  },
  okText: {
    type: String,
    default: '确定',
  },
  maskClosable: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:visible', 'cancel', 'ok'])

const handleCancel = () => {
  emit('cancel')
}

const handleConfirm = () => {
  emit('ok')
}

const handleClose = () => {
  if (!props.maskClosable) return
  emit('update:visible', false)
}
</script>

<style scoped lang="scss">
.action-confirm-dialog {
  position: fixed;
  inset: 0;
  z-index: 3200;
  display: flex;
  align-items: center;
  justify-content: center;

  &__mask {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
  }

  &__panel {
    position: relative;
    z-index: 1;
    width: calc(100vw - 64px);
    min-width: 270px;
    max-width: 312px;
    box-sizing: border-box;
    overflow: hidden;
    border-radius: 8px;
    background: $bg-white;
  }

  &__header {
    padding: 20px 16px;
    border-bottom: 1px solid $border-light-color;
    text-align: center;
  }

  &__title {
    color: $text-title-color;
    font-size: 17px;
    font-weight: 500;
    line-height: 26px;
    white-space: normal;
    word-break: break-word;
  }

  &__content {
    width: 100%;
    margin-top: 8px;
    color: $text-content-color;
    font-size: 15px;
    font-weight: 400;
    line-height: 21px;
    text-align: center;
  }

  &__actions {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
    height: 44px;

    button {
      border: 0;
      border-right: 1px solid $border-light-color;
      background: $bg-white;
      color: $primary-color;
      font-size: 16px;
      font-weight: 400;
      cursor: pointer;

      &:last-child {
        border-right: 0;
      }
    }
  }
}

.action-confirm-dialog-enter-active,
.action-confirm-dialog-leave-active {
  transition: opacity 0.18s ease;
}

.action-confirm-dialog-enter-from,
.action-confirm-dialog-leave-to {
  opacity: 0;
}
</style>
